import { useTranslation } from "@ca-dmv-radv/translation";
import {
  Alert,
  ALERT_WARNING,
  ICON_WARNING,
  Button,
  BUTTON_ICON_LEFT,
  BUTTON_STYLE_CTA,
  BUTTON_STYLE_SECONDARY,
  ICON_TAKE_PHOTO,
} from "@ca-dmv/core";
import { Modal } from "@ca-dmv/modal";
import { React } from "react";
import AnalyticsButton from "../analytics-button/AnalyticsButton";

const imageStyle = {
  height: "150px",
};

export default function AIDDocumentCapture({
  documentName,
  onClose,
  onCapture,
  onConfirm,
  requiredPages,
  document,
  applicationType,
}) {
  const { t } = useTranslation();
  const doc = document;

  const buttonLabel = (page) => {
    if (page === "front") {
      if (document.isFront) {
        return "Retake Front Photo";
      }
      return "Take Front Photo";
    }
    if (document.isBoth) {
      return "Retake Photos";
    }
    return "Take Photo";
  };

  const warning = t(
    "screens-Dashboard-Modal-background-warning",
    "Take photos on a dark background. Do not use flash."
  );

  const takePhotoButtonClass =
    "width--100 min-width--150 max-width--400 bp-md:w--auto border-radius--md border--md border--blue text--700";

  const takePhoto = (page) => {
    doc.page = page;
    doc.accessCamera = true;
    doc.retakeFront = false;
    doc.retakeBoth = false;

    if (page === "front" && document.isFront) {
      doc.isFront = false;
      doc.frontImage = null;
      doc.retakeFront = true;
    }
    if (page === "both" && document.isBoth) {
      doc.isBoth = false;
      doc.frontImage = null;
      doc.backImage = null;
      doc.retakeBoth = true;
    }

    return onCapture();
  };

  const confirmPhoto = () => {
    onConfirm();
  };
  return (
    <Modal
      onClose={onClose}
      modalTitle={t(
        "screens-Dashboard-Modal-RequiredPhotos-modalTitle",
        "Required Photos"
      )}
      contentContainerClass="modal-max-height"
      style={{ maxHeight: "75vh", overflowY: "auto" }}
    >
      <p className="text--xmd text--lh-sm text--blue-dark-2">
        {!document.multiplePages &&
          t(
            "screens-Dashboard-Modal-RequirePhotos-subtitle",
            "Please take a photo of your {{documentName}}",
            {
              documentName,
            }
          )}
        {document.multiplePages &&
          t(
            "screens-Dashboard-Modal-RequirePhotos-subtitle-both",
            "Take a photo of the front and back of your {{documentName}}.",
            {
              documentName: documentName?.toLowerCase(),
            }
          )}
      </p>
      {warning !== null && warning.trim() && (
        <Alert
          alertStyle={ALERT_WARNING}
          iconClass="icon--xsm mr-5"
          content={warning}
          contentClass="text--xxsm"
          alertIcon={ICON_WARNING}
          alertAlt="warning"
        />
      )}
      <ul className="list--clean p-0 m-0">
        {!document.multiplePages && (
          <li className="mt-16">
            <div className="flex flex--align-center flex--justify-center">
              <img
                className="mb-16"
                style={imageStyle}
                src={
                  document.isFront
                    ? document.frontImage?.dataUrl
                    : `${process.env.REACT_APP_IMAGE_PATHS}/img/icons/aid_camera_icon.svg`
                }
                alt="camera icon"
              />
            </div>

            <div className="flex flex--align-center flex--justify-center">
              <AnalyticsButton
                label={document.isFront ? "Retake Photo" : "Take Photo"}
                icon={ICON_TAKE_PHOTO}
                iconPosition={BUTTON_ICON_LEFT}
                btnStyle={BUTTON_STYLE_CTA}
                buttonClass={takePhotoButtonClass}
                onClick={() => takePhoto("front")}
                iconAlt="Capture"
                applicationType={applicationType}
                analyticsName="Take Photo Required Photos Dialog" analyticsAction="Take Photo" analyticsComponent="AIDDocumentCapture"
              />
            </div>
          </li>
        )}
        {document.multiplePages && (
          <>
            {document.isBoth &&
              requiredPages.map((page) => (
                <li key={page} className="mb-16">
                  <div className="flex flex--align-center flex--justify-center">
                    {page === "front" && (
                      <img
                        className="mb-16"
                        style={imageStyle}
                        src={
                          document.isBoth
                            ? document.frontImage?.dataUrl
                            : `${process.env.REACT_APP_IMAGE_PATHS}/img/icons/aid_camera_icon.svg`
                        }
                        alt="camera icon"
                      />
                    )}
                    {page === "back" && (
                      <img
                        className="mb-16"
                        style={imageStyle}
                        src={
                          document.isBoth
                            ? document.backImage?.dataUrl
                            : `${process.env.REACT_APP_IMAGE_PATHS}/img/icons/aid_camera_icon.svg`
                        }
                        alt="camera icon"
                      />
                    )}
                  </div>
                  <div className="flex flex--align-center flex--justify-center">
                    {`${page.charAt(0).toUpperCase() + page.slice(1)} Photo`}
                  </div>
                </li>
              ))}
            <div className="flex flex--align-center flex--justify-center">
              <AnalyticsButton
                label={buttonLabel("both")}
                icon={ICON_TAKE_PHOTO}
                iconPosition={BUTTON_ICON_LEFT}
                btnStyle={BUTTON_STYLE_CTA}
                buttonClass={takePhotoButtonClass}
                onClick={() => takePhoto("both")}
                iconAlt="Capture"
                applicationType={applicationType}
                analyticsName="Capture Required Photos Dialog" analyticsAction="Capture" analyticsComponent="AIDDocumentCapture"
              />
            </div>
          </>
        )}
      </ul>

      <div className="flex flex--align-center flex--justify-between mt-20">
        <AnalyticsButton
          label={t(
            "screens-Dashboard-Modal-TakePhotoWaiting-buttonCancelLabel",
            "Cancel"
          )}
          btnStyle={BUTTON_STYLE_SECONDARY}
          onClick={onClose}
          applicationType={applicationType}
          analyticsName="Cancel Required Photos Dialog" analyticsAction="Cancel" analyticsComponent="AIDDocumentCapture"
        />
        <AnalyticsButton
          label={t(
            "screens-Dashboard-Modal-TakePhotoWaiting-buttonOkLabel",
            "Submit"
          )}
          onClick={() => confirmPhoto()}
          disabled={
            (document.multiplePages && !document.isBoth) ||
            (!document.multiplePages && !document.isFront)
          }
          applicationType={applicationType}
          analyticsName="Submit Required Photos Dialog" analyticsAction="Submit" analyticsComponent="AIDDocumentCapture"
        />
      </div>
    </Modal>
  );
}
